/* Bo's Garage: How it works steps, kit finder, fitment check */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const touch = window.matchMedia('(pointer: coarse)').matches;
  // Scroll work runs once per frame, never straight from the scroll event.
  const onScroll = (fn) => {
    let queued = false;
    window.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; fn(); });
    }, { passive: true });
  };

  // How it works: cycles 1 → 2 → 3 while visible; a click picks a step and pauses for a while.
  document.querySelectorAll('[data-bos-hiw]').forEach((root) => {
    const steps = [...root.querySelectorAll('[data-hiw-step]')];
    let active = reduced ? 3 : 1;
    let timer = null;
    let pausedUntil = 0;
    const show = (n) => {
      active = n;
      root.dataset.active = String(n);
      steps.forEach((step) => step.classList.toggle('is-active', Number(step.dataset.hiwStep) === n));
    };
    const tick = () => { if (Date.now() >= pausedUntil) show(active % 3 + 1); };
    steps.forEach((step) => {
      const pick = () => { pausedUntil = Date.now() + 9000; show(Number(step.dataset.hiwStep)); };
      step.addEventListener('click', pick);
      step.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); pick(); } });
    });
    show(active);
    if (reduced) return;
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !timer) timer = setInterval(tick, 2800);
        if (!entry.isIntersecting && timer) { clearInterval(timer); timer = null; }
      });
    }, { threshold: .35 }).observe(root);
  });

  // Kit finder: tow vehicle tires + trailer tires → smallest kit that covers them.
  document.querySelectorAll('[data-bos-kits]').forEach((root) => {
    const kits = JSON.parse(root.querySelector('[data-kits-json]').textContent);
    const count = root.querySelector('[data-kits-count]');
    const kitName = root.querySelector('[data-kits-kit]');
    const action = root.querySelector('[data-kits-action]');
    const cards = [...root.querySelectorAll('[data-kit-card]')];
    const value = (name) => Number(root.querySelector(`input[name="${name}"]:checked`)?.value || 0);
    let match = null;

    const update = () => {
      const tires = value('bos-tow') + value('bos-trailer');
      match = tires ? kits.find((kit) => kit.wheels >= tires) : null;
      cards.forEach((card) => card.classList.toggle('is-match', !!match && Number(card.dataset.kitCard) === match.wheels));
      if (!tires) {
        count.textContent = root.dataset.textPick;
        kitName.textContent = '';
        action.hidden = true;
        return;
      }
      count.textContent = root.dataset.textCount.replace('[count]', tires);
      if (match) {
        kitName.textContent = root.dataset.textKit.replace('[wheels]', match.wheels);
        action.hidden = false;
        action.href = match.url;
      } else {
        kitName.textContent = root.dataset.textTooMany;
        action.hidden = true;
      }
    };

    // On the product page pick the variant in the main form instead of reloading.
    action.addEventListener('click', (event) => {
      if (!match) return;
      const form = document.querySelector('.shopify-section--main-product');
      // The theme's option radios carry the option value ID; match on the visible label text instead.
      const label = (input) => (input.closest('label') || form.querySelector(`label[for="${CSS.escape(input.id)}"]`))?.textContent.trim();
      const radio = form && [...form.querySelectorAll('input[type="radio"][data-option-position="1"]')]
        .find((input) => input.value === match.option || label(input) === match.option);
      if (radio) {
        event.preventDefault();
        radio.click();
        form.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }
    });
    root.addEventListener('change', update);
    update();
  });

  // Fitment: make → model → engine → year from the supplier compatibility list.
  document.querySelectorAll('[data-bos-fit]').forEach((root) => {
    const rows = JSON.parse(root.querySelector('[data-fit-json]').textContent);
    const [make, model, engine, year] = ['make', 'model', 'engine', 'year'].map((name) => root.querySelector(`[data-fit="${name}"]`));
    const result = root.querySelector('[data-fit-result]');
    const fill = (select, values, placeholder) => {
      select.innerHTML = `<option value="">${placeholder}</option>` + values.map((v) => `<option>${v}</option>`).join('');
      select.disabled = values.length === 0;
    };
    const uniq = (list) => [...new Set(list)];
    const pick = () => rows.filter((r) => (!make.value || r.make === make.value) && (!model.value || r.model === model.value) && (!engine.value || r.engine === engine.value));

    fill(make, uniq(rows.map((r) => r.make)).sort(), make.dataset.placeholder);
    fill(model, [], model.dataset.placeholder);
    fill(engine, [], engine.dataset.placeholder);
    fill(year, [], year.dataset.placeholder);

    root.addEventListener('change', (event) => {
      const changed = event.target.dataset.fit;
      if (changed === 'make') {
        fill(model, uniq(pick().map((r) => r.model)).sort(), model.dataset.placeholder);
        fill(engine, [], engine.dataset.placeholder);
        fill(year, [], year.dataset.placeholder);
      }
      if (changed === 'model') {
        fill(engine, uniq(pick().map((r) => r.engine)), engine.dataset.placeholder);
        fill(year, [], year.dataset.placeholder);
      }
      if (changed === 'engine') {
        const years = [];
        pick().forEach((r) => { for (let y = r.year_to; y >= r.year_from; y -= 1) years.push(y); });
        fill(year, uniq(years).sort((a, b) => b - a), year.dataset.placeholder);
      }
      result.classList.remove('is-ok');
      if (make.value && model.value && engine.value && year.value) {
        result.textContent = root.dataset.textOk.replace('[vehicle]', `${year.value} ${make.value} ${model.value} ${engine.value}`);
        result.classList.add('is-ok');
      } else {
        result.textContent = root.dataset.textHelp;
      }
    });
  });

  // Fitment finder: make → model → year → engine, with a clear verdict.
  document.querySelectorAll('[data-bos-finder]').forEach((root) => {
    const rows = JSON.parse(root.querySelector('[data-fit-json]').textContent);
    const [make, model, year, engine] = ['make', 'model', 'year', 'engine'].map((name) => root.querySelector(`[data-finder="${name}"]`));
    const result = root.querySelector('[data-finder-result]');
    const verdict = root.querySelector('[data-finder-verdict]');
    const warning = root.querySelector('[data-finder-warning]');
    const sub = root.querySelector('[data-finder-sub]');
    const buy = root.querySelector('[data-finder-buy]');
    const uniq = (list) => [...new Set(list)];
    const fill = (select, values) => {
      select.innerHTML = `<option value="">${select.dataset.placeholder}</option>` + values.map((v) => `<option>${v}</option>`).join('');
      select.disabled = values.length === 0;
    };
    const byModel = () => rows.filter((r) => r.make === make.value && r.model === model.value);
    const byYear = () => byModel().filter((r) => Number(year.value) >= r.year_from && Number(year.value) <= r.year_to);

    fill(make, uniq(rows.map((r) => r.make)).sort());
    [model, year, engine].forEach((select) => fill(select, []));

    const render = () => {
      // The supplier excludes 2019+ Silverado/Sierra V8s with the 10-speed; flag it whenever those trucks are picked.
      const truck = /Silverado|Sierra/.test(model.value);
      const complete = make.value && model.value && year.value && engine.value;
      warning.hidden = !truck || !!complete;
      if (complete) {
        result.dataset.state = 'ok';
        verdict.textContent = root.dataset.textOk.replace('[vehicle]', `${year.value} ${model.value} ${engine.value}`);
        // Replay the "pop" each time a new match is shown.
        result.classList.remove('is-pop');
        void result.offsetWidth;
        result.classList.add('is-pop');
      } else {
        result.dataset.state = truck ? 'warn' : 'pick';
        verdict.textContent = root.dataset.textPick;
      }
      sub.hidden = !complete;
      if (buy) buy.hidden = !complete;
    };
    // Order now: the same click as the product's Add to cart, so the bundle and the cart drawer behave as usual.
    buy?.addEventListener('click', () => {
      if (!buy.hasAttribute('data-finder-order')) return;
      root.closest('.shopify-section')?.querySelector('form[action*="/cart/add"] [type="submit"]')?.click();
    });

    root.addEventListener('change', (event) => {
      const changed = event.target.dataset.finder;
      if (changed === 'make') {
        fill(model, uniq(rows.filter((r) => r.make === make.value).map((r) => r.model)).sort());
        fill(year, []);
        fill(engine, []);
      }
      if (changed === 'model') {
        const years = [];
        byModel().forEach((r) => { for (let y = r.year_to; y >= r.year_from; y -= 1) years.push(y); });
        fill(year, uniq(years).sort((a, b) => b - a));
        fill(engine, []);
      }
      if (changed === 'year') {
        const engines = uniq(byYear().map((r) => r.engine));
        fill(engine, engines);
        if (engines.length === 1) engine.value = engines[0];
      }
      render();
    });
    render();
  });

  // How it works: scroll progress through the section fills the rail and picks the step; a tap picks it too.
  document.querySelectorAll('[data-bos-story]').forEach((root) => {
    const steps = [...root.querySelectorAll('[data-story-state]')];
    const fill = root.querySelector('.bos-story__fill');
    const count = root.querySelector('[data-story-count]');
    let current = null;
    const show = (index) => {
      if (index === current) return;
      current = index;
      steps.forEach((s, i) => s.classList.toggle('is-active', i === index));
      root.dataset.state = steps[index].dataset.storyState;
      if (count) count.textContent = index + 1;
      root.classList.toggle('is-last', index === steps.length - 1);
    };
    steps.forEach((step, i) => {
      step.addEventListener('click', () => show(i));
      step.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); show(i); } });
    });
    show(0);
    if (reduced) return;
    // Pin the stage under the sticky header and give it extra scroll room; scrolling that room walks the steps.
    const stage = root.querySelector('.bos-story__stage');
    let extra = 0;
    // Same height the stage sticks under (CSS --sticky-area-height: sticky announcement bar + header).
    const headerOffset = () => ['.shopify-section--announcement-bar', '.shopify-section--header']
      .map((selector) => document.querySelector(selector))
      .reduce((sum, el) => sum + (el && getComputedStyle(el).position === 'sticky' ? el.offsetHeight : 0), 0);
    const layout = () => {
      // Exact text heights: the opening and closing steps animate evenly, so the stage doesn't bounce on a step change.
      root.querySelectorAll('.bos-story__step p').forEach((text) => text.style.setProperty('--bos-step-h', `${text.scrollHeight}px`));
      root.style.height = '';
      const room = window.innerHeight - headerOffset();
      const fits = stage.offsetHeight <= room;
      root.classList.toggle('is-pinned', fits);
      // Scroll room per section, in screen heights (data-story-room; 1.8 by default).
      extra = fits ? Math.round(window.innerHeight * Number(root.dataset.storyRoom || 1.8)) : 0;
      if (fits) root.style.height = `${stage.offsetHeight + extra}px`;
    };
    const update = () => {
      const rect = root.getBoundingClientRect();
      // While the story is pinned, tell the CSS so the fixed Add to cart can step aside on short phones.
      document.documentElement.classList.toggle('bos-story-pinned', extra > 0 && rect.top <= headerOffset() && rect.bottom > stage.offsetHeight);
      const progress = extra
        ? Math.min(1, Math.max(0, (headerOffset() - rect.top) / extra))
        : Math.min(1, Math.max(0, (window.innerHeight * .6 - rect.top) / rect.height));
      if (fill) fill.style.transform = `scaleY(${progress.toFixed(3)})`;
      show(Math.min(steps.length - 1, Math.floor(progress * steps.length * .999)));
    };
    onScroll(update);
    // Phone browsers fire resize when their toolbar hides on scroll: the page height must not change then.
    let width = window.innerWidth;
    window.addEventListener('resize', () => {
      if (!touch || window.innerWidth !== width) { width = window.innerWidth; layout(); }
      update();
    });
    layout();
    update();
    document.fonts?.ready.then(() => { layout(); update(); });
  });

  // Price counter: counts up once when it scrolls into view.
  document.querySelectorAll('[data-bos-counter]').forEach((root) => {
    if (reduced || !('IntersectionObserver' in window)) return;
    const numbers = [...root.querySelectorAll('[data-count-to]')];
    const format = (el, value) => {
      const decimals = Number(el.dataset.countDecimals || 0);
      el.textContent = value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: el.dataset.countGroup === 'true' });
    };
    numbers.forEach((el) => format(el, 0));
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / 1400);
        const eased = 1 - Math.pow(1 - t, 3);
        numbers.forEach((el) => format(el, Number(el.dataset.countTo) * eased));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      // Frames can be throttled (background tab); always land on the exact values.
      setTimeout(() => numbers.forEach((el) => format(el, Number(el.dataset.countTo))), 1500);
    }, { threshold: .5 });
    observer.observe(root);
  });

  // Offer countdown; at zero every offer element on the page hides. Countdowns added later (a variant change
  // re-renders the buy box) start too.
  const pad = (n) => String(n).padStart(2, '0');
  const startCountdown = (root) => {
    if (root.dataset.bosCdStarted) return;
    root.dataset.bosCdStarted = '1';
    const end = Date.parse(root.dataset.ends);
    if (Number.isNaN(end)) { root.hidden = true; return; }
    const parts = Object.fromEntries([...root.querySelectorAll('[data-cd]')].map((el) => [el.dataset.cd, el]));
    const tick = () => {
      if (!root.isConnected) { clearInterval(timer); return; }
      const left = Math.max(0, end - Date.now());
      if (!left) {
        clearInterval(timer);
        document.querySelectorAll('.bos-ann-halloween, .bos-halloween').forEach((el) => { el.hidden = true; });
        document.body.removeAttribute('data-bos-halloween');
        return;
      }
      const s = Math.floor(left / 1000);
      parts.d.textContent = Math.floor(s / 86400);
      parts.h.textContent = pad(Math.floor(s / 3600) % 24);
      parts.m.textContent = pad(Math.floor(s / 60) % 60);
      parts.s.textContent = pad(s % 60);
    };
    const timer = setInterval(tick, 1000);
    tick();
  };
  document.querySelectorAll('[data-bos-countdown]').forEach(startCountdown);
  new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
    if (node.nodeType !== 1) return;
    if (node.matches('[data-bos-countdown]')) startCountdown(node);
    node.querySelectorAll('[data-bos-countdown]').forEach(startCountdown);
  }))).observe(document.body, { childList: true, subtree: true });

  // Product gallery: slides past the second fetch only when they come near the screen or the zoom opens.
  document.querySelectorAll('media-carousel').forEach((carousel) => {
    const pending = [...carousel.querySelectorAll('img[data-bos-src]')];
    if (!pending.length) return;
    const release = (img) => {
      if (!img.dataset.bosSrc) return;
      img.srcset = img.dataset.bosSrcset || '';
      img.src = img.dataset.bosSrc;
      delete img.dataset.bosSrc;
      delete img.dataset.bosSrcset;
    };
    if (!('IntersectionObserver' in window)) { pending.forEach(release); return; }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      release(entry.target);
    }), { root: carousel, rootMargin: '0px 60% 0px 60%' });
    pending.forEach((img) => observer.observe(img));
    // A thumbnail or dot picks a distant slide: load it and its neighbours right away, before the scroll gets there.
    carousel.addEventListener('carousel:select', (event) => {
      const slide = event.detail?.slide;
      [slide, slide?.previousElementSibling, slide?.nextElementSibling].forEach((item) => {
        item?.querySelectorAll('img[data-bos-src]').forEach(release);
      });
    });
    // The zoom builds its slides from these images, so all of them must be real by then.
    carousel.closest('product-gallery')?.addEventListener('lightbox:open', () => pending.forEach(release), true);
  });

  // Home product cards: the price follows the selected kit.
  document.querySelectorAll('[data-bos-quick-add]').forEach((form) => {
    const select = form.querySelector('[data-bos-variant]');
    if (!select) return;
    select.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      form.querySelector('[data-bos-price]').textContent = option.dataset.price;
      form.querySelector('[data-bos-compare]').textContent = option.dataset.compare;
    });
  });

  // TPMS kit picker: a wheel count lights the rig, sets sensors, price and link.
  document.querySelectorAll('[data-bos-kitpick]').forEach((root) => {
    const buttons = [...root.querySelectorAll('[data-kit]')];
    const stage = root.querySelector('.bos-kitpick__stage');
    const pick = (button) => {
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      stage.dataset.kitActive = button.dataset.kit;
      root.querySelector('[data-kit-out-count]').textContent = button.dataset.kit;
      root.querySelector('[data-kit-out-n]').textContent = button.dataset.kit;
      root.querySelector('[data-kit-out-price]').textContent = button.dataset.kitPrice;
      root.querySelector('[data-kit-out-text]').textContent = button.dataset.kitText;
      root.querySelector('[data-kit-out-cta]').href = button.dataset.kitUrl;
    };
    buttons.forEach((button) => button.addEventListener('click', () => pick(button)));
    pick(buttons.find((b) => b.getAttribute('aria-pressed') === 'true') || buttons[0]);
  });

  // Tabs (home "How it works").
  document.querySelectorAll('[data-bos-tabs]').forEach((root) => {
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const select = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        root.querySelector(`[data-panel="${t.dataset.tab}"]`).hidden = !on;
      });
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const next = tabs[(i + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        select(next);
        next.focus();
      });
    });
  });

  // Home hero: light parallax on Bo's photo; icons fade in one by one once visible.
  document.querySelectorAll('[data-bos-hero]').forEach((root) => {
    if (reduced) { root.classList.add('is-in'); return; }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries, observer) => {
        if (entries.some((e) => e.isIntersecting)) { root.classList.add('is-in'); observer.disconnect(); }
      }, { threshold: .2 }).observe(root);
    } else root.classList.add('is-in');
    const layer = root.querySelector('[data-bos-parallax]');
    // Script-driven parallax trembles against native touch scrolling, so it stays on mouse devices.
    if (!layer || touch) return;
    onScroll(() => {
      const top = root.getBoundingClientRect().top;
      if (top > window.innerHeight || top < -root.offsetHeight) return;
      layer.style.transform = `translate3d(0, ${Math.round(top * -0.08)}px, 0)`;
    });
  });

  // Sale fly-by: leaves fall, sway and spin with the scroll, each fading out on its way down;
  // the whole layer fades when its section (the stop point) comes up. Reduced motion: the leaves stay still.
  document.querySelectorAll('[data-bos-flyby]').forEach((layer) => {
    if (reduced) return;
    const marker = layer.parentElement;
    const leaves = [...layer.querySelectorAll('.bos-flyby__item')].map((el) => ({
      el,
      rate: Number(el.dataset.rate) || .6,
      spin: Number(el.dataset.spin) || .3,
      phase: Number(el.dataset.phase) || 0,
      rot: Number(el.dataset.rot) || 0,
      dx: Number(el.dataset.dx) || 0,
      side: el.classList.contains('bos-flyby__item--r') ? -1 : 1,
    }));
    const update = () => {
      const gone = marker.getBoundingClientRect().top < window.innerHeight * .65;
      layer.classList.toggle('is-gone', gone);
      if (gone) return;
      const y = window.scrollY;
      const fadeAfter = window.innerHeight * .7;
      const phone = window.innerWidth < 1000;
      const sway = phone ? 10 : 22;
      leaves.forEach((leaf) => {
        const fall = y * leaf.rate;
        // Start from the leaf's own offset and angle, so the first screen already looks scattered.
        const x = leaf.dx * (phone ? .5 : 1) + leaf.side * sway * (Math.sin(fall / 80 + leaf.phase) - Math.sin(leaf.phase));
        leaf.el.style.transform = `translate3d(${x.toFixed(1)}px, ${fall.toFixed(1)}px, 0) rotate(${(leaf.rot + fall * leaf.spin).toFixed(1)}deg)`;
        leaf.el.style.opacity = String(Math.max(0, 1 - fall / fadeAfter).toFixed(2));
      });
    };
    onScroll(update);
    update();
  });

  // Comparison: ticks and crosses pop in row by row once the table is visible.
  document.querySelectorAll('.bos-compare__table').forEach((table) => {
    if (reduced || !('IntersectionObserver' in window)) return;
    table.classList.add('is-waiting');
    new IntersectionObserver((entries, observer) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      observer.disconnect();
      table.classList.add('is-in');
    }, { threshold: .3 }).observe(table);
  });
})();
